/**
 * Maps backend HTTP status codes and error messages from validate-otp API to i18n translation keys
 * Used by VerifyOtpStep to show appropriate user-friendly error messages
 *
 * Backend error scenarios from STO-2085:
 * - 400: Invalid request (channel, identifier, otp, correlationId)
 * - 401: OTP validation failed (wrong code, expired, max attempts exceeded, not found)
 * - 403: Customer not found or suspended
 * - 503: Feature flag disabled
 *
 * NOTE: Backend returns generic 401 for all OTP validation failures and does NOT distinguish
 * between wrong code, expired code, max attempts exceeded, etc. This is a security measure
 * to prevent account enumeration attacks. Frontend cannot determine the exact reason from
 * the HTTP status alone.
 *
 * COUPLING WARNING (M3):
 * This mapper couples to backend error message prose because the backend does not
 * return structured error codes. We match on message.toLowerCase().includes() patterns.
 * If backend wording changes, this will silently degrade to generic 'unexpectedError'.
 *
 * Backend strings we depend on (see verifyOtpErrorCodeMapper.test.ts for exact examples):
 * - 400 otp format: "otp", "digit"
 * - 400 channel: "channel"
 * - 400 identifier: "identifier"
 * - 400 correlationId: "correlation"
 * - 403 suspended: "suspended"
 * - 403 not found: "not found"
 * - 503 feature: "feature", "available"
 *
 * FUTURE: Prefer backend to return error.code (e.g. "INVALID_OTP", "OTP_EXPIRED", "ACCOUNT_SUSPENDED")
 * and map on that instead. Until then, test coverage (11 tests) catches wording drift.
 */

interface NormalizedError {
	status: number
	message?: string
}

export function getVerifyOtpErrorMessageKey(error: NormalizedError): string {
	const { status, message = '' } = error
	const messageLower = message.toLowerCase()

	// ===== 400 BAD REQUEST =====
	if (status === 400) {
		// Invalid OTP format (not 6 digits)
		if (messageLower.includes('otp') || messageLower.includes('digit')) {
			return 'signin:errors:unexpectedError' // FE pre-check handles this
		}

		// Invalid channel
		if (messageLower.includes('channel')) {
			return 'signin:errors:invalidChannel'
		}

		// Invalid identifier
		if (messageLower.includes('identifier')) {
			return 'signin:errors:unexpectedError'
		}

		// Missing correlationId (rare, user interaction wouldn't cause this)
		if (messageLower.includes('correlation')) {
			return 'signin:errors:sessionExpired'
		}

		return 'signin:errors:unexpectedError'
	}

	// ===== 401 UNAUTHORIZED =====
	// Backend returns 401 for ALL OTP validation failures:
	// - OTP not found
	// - OTP expired (> 10 min)
	// - Wrong OTP code (bcrypt mismatch)
	// - Attempt count exceeded (5+ wrong attempts)
	//
	// Backend does NOT distinguish between these cases (security by obscurity)
	// so we cannot determine the exact reason on the frontend.
	// All 401 errors result in the same "failure" UI.
	if (status === 401) {
		// Generic 401 — could be wrong code, expired, or max attempts
		// Show generic message that covers all cases
		return 'signin:errors:incorrectCode'
	}

	// ===== 403 FORBIDDEN =====
	if (status === 403) {
		// Customer not found or account suspended
		if (messageLower.includes('suspended')) {
			return 'signin:errors:accountSuspended'
		}
		if (messageLower.includes('not found')) {
			return 'signin:errors:accountNotFound'
		}
		// Generic 403
		return 'signin:errors:accountNotFound'
	}

	// ===== 503 SERVICE UNAVAILABLE =====
	if (status === 503) {
		// Feature flag disabled
		if (messageLower.includes('feature') || messageLower.includes('available')) {
			return 'signin:errors:featureDisabled'
		}
		// Fallback for 503
		return 'signin:errors:featureDisabled'
	}

	// ===== NETWORK ERRORS =====
	if (!status || status === 0) {
		return 'signin:errors:networkError'
	}

	// ===== UNEXPECTED STATUS CODES =====
	return 'signin:errors:unexpectedError'
}
